import { useEffect, useMemo, useState } from 'react'
import { client } from '../lib/sanity'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import moment from 'moment'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import { logout } from '../utils/auth'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import Button from '../components/ui/Button'
import EventModal from '../components/calendar/EventModal'
import DayAgendaView from '../components/calendar/DayAgendaView'
import { expandEvents, moveOccurrence } from '../lib/recurrence'

const localizer = momentLocalizer(moment)
const DnDCalendar = withDragAndDrop(Calendar)

const defaultRange = (anchor) => {
    const m = moment(anchor || undefined)
    return {
        start: m.clone().startOf('month').subtract(1, 'week').toDate(),
        end: m.clone().endOf('month').add(1, 'week').toDate(),
    }
}

export default function AdminCalendar() {
    const navigate = useNavigate()
    const location = useLocation()
    const focusDate = location.state?.focusDate ? new Date(location.state.focusDate) : null
    const [rawDocs, setRawDocs] = useState([])
    const [categories, setCategories] = useState([])
    const [visibleRange, setVisibleRange] = useState(() => defaultRange(focusDate))
    const [slot, setSlot] = useState(null)
    const [selectedEvent, setSelectedEvent] = useState(null)
    const [modalOpen, setModalOpen] = useState(false)

    useEffect(() => {
        refreshEvents()
        refreshCategories()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const refreshEvents = async () => {
        const data = await client.fetch(
            `*[_type == "calendarEvent"]{..., category->{_id, title, color, isMilestone}}`
        )
        setRawDocs(data)
    }

    const refreshCategories = async () => {
        const data = await client.fetch(`*[_type == "eventCategory"] | order(title asc)`)
        setCategories(data)
    }

    const events = useMemo(
        () => expandEvents(rawDocs, visibleRange.start, visibleRange.end),
        [rawDocs, visibleRange]
    )

    const handleRangeChange = (range) => {
        const [start, end] = Array.isArray(range) ? [range[0], range[range.length - 1]] : [range.start, range.end]
        setVisibleRange({
            start: moment(start).subtract(1, 'week').toDate(),
            end: moment(end).add(1, 'week').toDate(),
        })
    }

    const handleSelectSlot = ({ start, end }) => {
        setSelectedEvent(null)
        setSlot({ start, end })
        setModalOpen(true)
    }

    const handleSelectEvent = (event) => {
        setSlot(null)
        setSelectedEvent(event)
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setSlot(null)
        setSelectedEvent(null)
    }

    const handleSaved = () => {
        closeModal()
        refreshEvents()
    }

    const handleEventChange = async ({ event, start, end }) => {
        await moveOccurrence(event, start, end)
        refreshEvents()
    }

    const eventPropGetter = (event) => {
        if (!event.category) return {}
        return {
            style: {
                backgroundColor: event.category.color,
                borderColor: event.category.color,
            },
        }
    }

    return (
        <div className="h-screen w-full flex flex-col overflow-hidden bg-white dark:bg-zinc-950">
            <div className="flex-shrink-0 flex justify-between items-center gap-3 px-3 py-2 border-b-2 border-black dark:border-white">
                <h2 className="text-lg font-serif font-bold text-black dark:text-white">Admin Calendar</h2>
                <div className="flex gap-2">
                    <Button as={Link} to="/admin/calendar/yearly" variant="secondary" className="!px-3 !py-1.5 text-xs">Yearly View →</Button>
                    <Button
                        variant="destructive"
                        className="!px-3 !py-1.5 text-xs"
                        onClick={async () => {
                            await logout()
                            navigate('/calendar')
                        }}
                    >
                        Logout
                    </Button>
                </div>
            </div>

            <div className="flex-1 min-h-0 p-2">
                <DnDCalendar
                    className="rbc-custom h-full"
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    selectable
                    resizable
                    draggableAccessor={() => true}
                    onSelectSlot={handleSelectSlot}
                    onSelectEvent={handleSelectEvent}
                    onRangeChange={handleRangeChange}
                    onEventDrop={handleEventChange}
                    onEventResize={handleEventChange}
                    eventPropGetter={eventPropGetter}
                    views={{ month: true, week: true, day: DayAgendaView }}
                    min={moment().hour(7).minute(0).second(0).toDate()}
                    max={moment().hour(22).minute(0).second(0).toDate()}
                    defaultDate={focusDate || undefined}
                    defaultView={focusDate ? 'day' : 'month'}
                    style={{ height: '100%' }}
                />
            </div>

            <EventModal
                open={modalOpen}
                onClose={closeModal}
                event={selectedEvent}
                slot={slot}
                categories={categories}
                onSaved={handleSaved}
                onDeleted={handleSaved}
                onCategoriesChanged={refreshCategories}
            />
        </div>
    )
}
